import { createContext, type ReactNode, use, useEffect, useState } from 'react';

interface SearchContextValue {
  /** Raw input value, updates on every keystroke. */
  rawSearch: string;
  /** Debounced value, updates ~250ms after the user stops typing. */
  search: string;
  setSearch: (value: string) => void;
  clearSearch: () => void;
}

const SearchContext = createContext<SearchContextValue | undefined>(undefined);

export function SearchProvider({ children }: { children: ReactNode }) {
  const [rawSearch, setRawSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(rawSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [rawSearch]);

  return (
    <SearchContext
      value={{
        rawSearch,
        search: debounced,
        setSearch: setRawSearch,
        clearSearch: () => setRawSearch(''),
      }}
    >
      {children}
    </SearchContext>
  );
}

export function useSearch(): SearchContextValue {
  const ctx = use(SearchContext);
  if (!ctx) throw new Error('useSearch must be used within <SearchProvider>');
  return ctx;
}
